/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { YospaceAdsSession, PlaybackMode } from '../YospaceAdsSession';
import { YospaceInitializationData, Status } from '../model/YospaceInitializationData';
import {
  createError,
  Logger,
  UtilFactory,
  createLogger,
  LoggerLevel,
  Error,
  ErrorCodes,
  CryptoJS,
} from '@quickplay/foundation';
import { YospaceLogger } from './YospaceLogger';
import {
  AdPlaybackPolicy,
  PlaybackAutoSeekMode,
  PlaybackFastForwardMode,
  PlaybackInterruptedMode,
  PlaybackRepeatedMode,
  PlaybackRewindMode,
} from '@quickplay/player-interface';
import { DefaultAdsPlaybackPolicy } from './vod/policy/DefaultAdsPlaybackPolicy';
import { YospacePlayer } from './YospacePlayer';
import { YospaceErrorCodes } from './YospaceErrorCodes';

const DEFAULT_USER_AGENT = 'FL_YOSPACE_ADS_PLAYER';
const TAG_NAME = 'FL_YOSPACE_SESSION';

export class YospaceSession implements YospaceAdsSession {
  initializationData!: YospaceInitializationData;
  internalSession: any;
  internalAdsPlaybackPolicy: any;
  logger: Logger;
  yospaceSDK: any;

  private deferred_ = new UtilFactory.Deferred<{ url: string; status: Status; reason?: string }>();

  constructor(yospaceSDK: any, logger?: Logger) {
    this.yospaceSDK = yospaceSDK;
    this.logger = logger || createLogger(LoggerLevel.OFF, TAG_NAME);
    this.sessionEventListener = this.sessionEventListener.bind(this);
  }

  initialize(
    initializationData: YospaceInitializationData,
    adPlaybackPolicy?: AdPlaybackPolicy,
  ): Promise<{ url: string; status: Status; reason?: string }> {
    this.deferred_ = new UtilFactory.Deferred<{ url: string; status: Status; reason?: string }>();
    this.initializationData = initializationData;

    // initialize Ad playback policy if available
    if (adPlaybackPolicy) {
      this.internalAdsPlaybackPolicy = new DefaultAdsPlaybackPolicy(adPlaybackPolicy, this.yospaceSDK, this.logger);
    } else {
      const defaultAdsPlaybackPolicyRules = {
        fastForwardMode: PlaybackFastForwardMode.TRICK_FW_PLAY_ALL,
        rewindMode: PlaybackRewindMode.TRICK_RW_PLAY_ALL,
        autoSeekMode: PlaybackAutoSeekMode.AUTO_SEEK_PLAY_PREROLL,
        repeatMode: PlaybackRepeatedMode.REPEAT_ALWAYS_PLAY,
        interruptMode: PlaybackInterruptedMode.INTERRUPT_RESUME,
      };

      this.internalAdsPlaybackPolicy = new DefaultAdsPlaybackPolicy(
        defaultAdsPlaybackPolicyRules,
        this.yospaceSDK,
        this.logger,
      );
    }

    this.initYospaceSession();

    return this.deferred_.promise;
  }

  shutdown(): void {
    if (this.internalSession) {
      this.internalSession.shutdown();
      this.internalSession = undefined;
    }
  }

  private initYospaceSession(): void {
    const properties = new this.yospaceSDK.SessionProperties();
    properties.userAgent = this.initializationData.userAgent ? this.initializationData.userAgent : DEFAULT_USER_AGENT;
    properties.allowCorsForAnalytics = this.initializationData.allowCorsForAnalytics;
    if (this.initializationData.timeout) {
      properties.setRequestTimeout(this.initializationData.timeout);
    }

    YospaceLogger.setLogger(this.logger);
    this.yospaceSDK.YoLog.setLogger(YospaceLogger);
    this.yospaceSDK.YoLog.setDebugFlags(this.yospaceSDK.DEBUG_ALL);

    if (this.initializationData) {
      switch (this.initializationData.playbackMode) {
        case PlaybackMode.Live:
        case PlaybackMode.LiveRestart: {
          this.yospaceSDK.SessionLive.create(this.initializationData.contentURL, properties, this.sessionEventListener);
          break;
        }
        case PlaybackMode.LiveCatchup:
        case PlaybackMode.Vod: {
          this.yospaceSDK.SessionVOD.create(this.initializationData.contentURL, properties, this.sessionEventListener);
          break;
        }
      }
    }
  }

  /**
   * Event listener function for the session events.
   * @param event the session event
   */
  sessionEventListener(event: any): void {
    this.internalSession = event.getPayload();

    switch (this.internalSession.getSessionState()) {
      case this.yospaceSDK.SessionState.INITIALISED: {
        const result = {
          url: this.session.playbackUrl,
          status: Status.SUCCESS,
        };
        if (this.session instanceof this.yospaceSDK.SessionVOD) {
          // set Ad playback policy handler to the Yospace VOD session
          if (this.internalAdsPlaybackPolicy) {
            this.internalSession.setPlaybackPolicyHandler(this.internalAdsPlaybackPolicy);
          }

          this.logger.log(`VOD Session playback URL: ${this.session.playbackUrl}`);

          this.deferred_.resolve(result);
        } else if (this.session instanceof this.yospaceSDK.SessionLive) {
          this.logger.info(`Live Session playback URL: ${this.session.playbackUrl}`);

          this.deferred_.resolve(result);
        }

        break;
      }
      case this.yospaceSDK.SessionState.NO_ANALYTICS: {
        const message = `Yospace Session initialization Failed with Status : ${
          Status.FALLBACK
        } and errorCode : ${this.internalSession.getResultCode()}`;
        this.logger.warn(message);
        const result = {
          url: this.session.playbackUrl,
          status: Status.FALLBACK,
          reason: message,
        };
        this.deferred_.resolve(result);
        break;
      }
      case this.yospaceSDK.SessionState.FAILED: {
        const message = `Yospace Session initialization Failed with Status : ${
          Status.ERROR
        } and errorCode : ${this.internalSession.getResultCode()}`;
        this.logger.warn(message);
        const fallbackUrlValue = this.getFallbackUrl(this.initializationData);
        if (fallbackUrlValue !== undefined) {
          const decodeBase64Url = CryptoJS.enc.Base64.parse(fallbackUrlValue);
          const fallbackUrl = CryptoJS.enc.Utf8.stringify(decodeBase64Url);
          const result = {
            url: fallbackUrl,
            status: Status.ERROR,
            reason: message,
          };
          this.deferred_.resolve(result);
        } else {
          const error = this.createSessionError(this.internalSession.getResultCode(), message);
          this.deferred_.reject(error);
        }
        break;
      }
      case this.yospaceSDK.SessionState.SHUTDOWN: {
        const message = `Session shutdown: ${this.yospaceSDK.SessionState.SHUTDOWN}`;
        this.logger.warn(message);
      }
    }
  }

  get session(): any {
    return this.internalSession;
  }

  get adsPlaybackPolicy(): any {
    return this.internalAdsPlaybackPolicy;
  }

  private getFallbackUrl = (initializationData: YospaceInitializationData): string | undefined => {
    const fallbackUrlParam = 'yo.eb.fb=';
    let fallbackUrl: string | undefined = undefined;

    if (initializationData && initializationData.contentURL) {
      const [, queryParams] = initializationData.contentURL.split('?');
      const urlParams: string[] = queryParams ? queryParams.split('&') : [];

      for (let i = 0; i < urlParams.length; i++) {
        if (urlParams[i].startsWith(fallbackUrlParam)) {
          [, fallbackUrl] = urlParams[i].split('=');
          break;
        }
      }
    }
    return fallbackUrl;
  };

  private createSessionError = (errorCode: number, message: string): Error => {
    const internalError = errorCode ? createError(errorCode, message) : undefined;
    return createError(
      YospaceErrorCodes.SESSION_INIT_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_THIRDPARTY,
      message,
      internalError,
    );
  };

  attachPlayerToPolicyHandler(player: YospacePlayer): void {
    if (this.internalAdsPlaybackPolicy) {
      this.internalAdsPlaybackPolicy.setPlayer(player);
    }
  }

  evaluateSeek(): void {
    if (this.internalAdsPlaybackPolicy) {
      this.internalAdsPlaybackPolicy.evaluateSeek(
        this.internalSession.getAdBreaksByType(this.yospaceSDK.BreakType.LINEAR),
      );
    }
  }
}
